/**
 * Página: Superadmin (nivel plataforma) — pública, sin sidebar.
 * Ruta temporal /superadmin (todavía sin subdominio propio). TOTALMENTE
 * aparte de la sesión de un municipio: el token se guarda bajo la clave
 * "superadmin_token" (NO "token"), así nunca pisa ni se mezcla con la
 * sesión de admin/operador de un municipio.
 *
 * Logueado, tiene tres pestañas: "Alta de cliente", "Clientes" (lista con el
 * estado de la invitación de cada admin + reenviarla; components/
 * SuperadminClientes.jsx) y "Seguridad". El alta crea un municipio
 * nuevo completo (base dedicada + registro + admin principal invitado por
 * mail) llamando a POST /api/superadmin/municipios. Mismo backend que el
 * script de CLI scripts/alta-municipio.js, y también re-corrible (mandar
 * la misma alta dos veces no duplica nada).
 *
 * Verificación en dos pasos (2FA por TOTP), opcional por cuenta: si el
 * backend responde `requiere_2fa` al loguear, en vez del JWT devuelve un
 * token temporal (2 minutos) y esta página pasa a pedir el código de 6
 * dígitos (POST /superadmin/2fa/verificar). Ya logueado, la pestaña
 * "Seguridad" (components/SuperadminSeguridad.jsx) permite activarlo.
 *
 * Mismo estilo visual que Login.jsx.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  STROKE_DEGRADE,
  IconEmail,
  IconContrasena,
  IconMostrarContrasena,
  IconOcultarContrasena,
  IconMunicipio,
  IconPersona,
  IconDocumento,
  IconExito,
  IconAdvertencia,
  IconCandado,
} from '../components/icons';
import SuperadminSeguridad from '../components/SuperadminSeguridad';
import SuperadminClientes from '../components/SuperadminClientes';
import SuperadminDemos from '../components/SuperadminDemos';
import { filtrarProvincias, resolverProvincia } from '../utils/provincias';
import { enmascararCuil, posicionCursorCuil } from '../utils/fecha';
import '../styles/login.css';

const API_URL = process.env.REACT_APP_API_URL;
const CLAVE_TOKEN = 'superadmin_token';

// Mensaje EXACTO con el que el backend rechaza un código de 6 dígitos mal
// tipeado en /2fa/verificar (401). Es lo único que distingue "te equivocaste
// de código, reintentá" de "el token temporal ya no sirve, volvé a empezar":
// ambos son 401. Si el texto cambiara en el backend, el fallo es hacia el
// lado seguro (se vuelve al login), nunca se deja pasar nada.
const MENSAJE_CODIGO_INCORRECTO = 'Código incorrecto';

// Vida (en segundos) del token temporal del paso de 2FA, leída de su propio
// payload (exp - iat) — se usa de reloj en pantalla, así no depende de que
// la hora de la PC coincida con la del servidor. No verifica nada: la
// validez real la decide siempre el backend.
const segundosDeVida = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp - payload.iat;
  } catch {
    return 120;
  }
};

const FORM_VACIO = {
  nombre: '',
  subdominio: '',
  provincia: '',
  adminNombre: '',
  adminApellido: '',
  adminCuil: '',
  adminEmail: '',
  adminTelefono: '',
};

const Superadmin = () => {
  // --- estado de login ---
  const [email, setEmail] = useState('');
  const [contrasena, setContrasena] = useState('');
  const [mostrarContrasena, setMostrarContrasena] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [me, setMe] = useState(null);

  // --- estado del segundo paso del login (2FA) ---
  const [token2fa, setToken2fa] = useState(null); // token temporal; null = no estamos en ese paso
  const [codigo2fa, setCodigo2fa] = useState('');
  const [verificando, setVerificando] = useState(false);

  // --- sección activa del panel ---
  const [seccion, setSeccion] = useState('alta'); // 'alta' | 'clientes' | 'demos' | 'seguridad'

  // --- estado del alta de cliente ---
  const [form, setForm] = useState(FORM_VACIO);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null); // { tipo: 'exito'|'aviso'|'error', mensaje, url?, adminEmail? }

  // --- combobox de Provincia (lista fija, sin texto libre) ---
  const [provinciaAbierta, setProvinciaAbierta] = useState(false);
  const [provinciaIdx, setProvinciaIdx] = useState(-1);
  const provinciasSugeridas = filtrarProvincias(form.provincia);
  const provinciaCanonica = resolverProvincia(form.provincia); // string | null

  const cargarMe = async (token) => {
    try {
      const { data } = await axios.get(`${API_URL}/superadmin/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMe(data);
    } catch (err) {
      // Token guardado inválido/vencido: limpiar y volver al formulario.
      console.error('Error en /superadmin/me:', err);
      localStorage.removeItem(CLAVE_TOKEN);
      setMe(null);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem(CLAVE_TOKEN);
    if (token) cargarMe(token);
  }, []);

  // Vuelve del paso de 2FA al formulario de email + contraseña, descartando
  // el token temporal. La contraseña se limpia (no tiene por qué quedar en
  // memoria); el email queda para no retipearlo.
  const volverAlLogin = (mensaje) => {
    setToken2fa(null);
    setCodigo2fa('');
    setContrasena('');
    setError(mensaje || '');
  };

  // El token temporal dura 2 minutos: si se agota antes de confirmar el
  // código, se avisa y se vuelve a empezar desde cero.
  useEffect(() => {
    if (!token2fa) return undefined;
    const timer = setTimeout(() => {
      volverAlLogin('Se agotó el tiempo para confirmar el código (2 minutos). Volvé a ingresar tu email y contraseña.');
    }, segundosDeVida(token2fa) * 1000);
    return () => clearTimeout(timer);
  }, [token2fa]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setCargando(true);
    try {
      const { data } = await axios.post(`${API_URL}/superadmin/login`, { email, contrasena });
      if (data.requiere_2fa) {
        // Todavía sin acceso: solo hay un token temporal. Se pasa al
        // segundo paso, sin guardar nada en localStorage.
        setCodigo2fa('');
        setToken2fa(data.token_2fa);
        return;
      }
      localStorage.setItem(CLAVE_TOKEN, data.token);
      await cargarMe(data.token);
    } catch (err) {
      setError(err.response?.data?.mensaje || 'Error al ingresar. Verificá tus datos.');
      console.error('Error de login superadmin:', err);
    } finally {
      setCargando(false);
    }
  };

  const handleVerificar2fa = async (e) => {
    e.preventDefault();
    if (codigo2fa.length !== 6) return;
    setError('');
    setVerificando(true);
    try {
      const { data } = await axios.post(`${API_URL}/superadmin/2fa/verificar`, {
        token_2fa: token2fa,
        codigo: codigo2fa,
      });
      // Desde acá es exactamente un login normal.
      localStorage.setItem(CLAVE_TOKEN, data.token);
      setToken2fa(null);
      setCodigo2fa('');
      setContrasena('');
      await cargarMe(data.token);
    } catch (err) {
      console.error('Error al verificar el código de 2FA:', err);
      const status = err.response?.status;
      const mensaje = err.response?.data?.mensaje;
      if (status === 401 && mensaje === MENSAJE_CODIGO_INCORRECTO) {
        // Se equivocó de código: se puede reintentar con el mismo token
        // temporal (mientras no venza).
        setError('El código no es correcto. Fijate el código actual de tu app (cambia cada 30 segundos) y probá de nuevo.');
        setCodigo2fa('');
      } else if (status === 401) {
        // El token temporal venció o dejó de servir: hay que empezar de nuevo.
        volverAlLogin(mensaje || 'La verificación venció. Volvé a ingresar tu email y contraseña.');
      } else {
        // 429 (demasiados intentos), 400, error de red, etc.
        setError(mensaje || 'No se pudo verificar el código. Probá de nuevo.');
      }
    } finally {
      setVerificando(false);
    }
  };

  const cerrarSesion = () => {
    localStorage.removeItem(CLAVE_TOKEN);
    setMe(null);
    setEmail('');
    setContrasena('');
    setToken2fa(null);
    setCodigo2fa('');
    setSeccion('alta');
    setForm(FORM_VACIO);
    setResultado(null);
  };

  const actualizarCampo = (campo) => (e) => {
    // El subdominio se fuerza a minúsculas mientras se tipea (es lo que
    // exige el backend) y se limpia de espacios.
    if (campo === 'subdominio') {
      const valor = e.target.value.toLowerCase().replace(/\s+/g, '');
      setForm((prev) => ({ ...prev, subdominio: valor }));
      return;
    }
    // CUIL: máscara en vivo XX-XXXXXXXX-X, funciona igual tipeando que
    // pegando un valor ya formateado o crudo, preservando el cursor.
    if (campo === 'adminCuil') {
      const input = e.target;
      const cursorPrevio = input.selectionStart;
      const formateado = enmascararCuil(input.value);
      const nuevaPosicion = posicionCursorCuil(input.value, cursorPrevio, formateado);
      setForm((prev) => ({ ...prev, adminCuil: formateado }));
      requestAnimationFrame(() => input.setSelectionRange(nuevaPosicion, nuevaPosicion));
      return;
    }
    setForm((prev) => ({ ...prev, [campo]: e.target.value }));
  };

  // --- Provincia: autocompletado contra la lista fija, sin texto libre ---
  const onProvinciaChange = (e) => {
    // Se muestra en mayúsculas (como va en la tarjeta) mientras se tipea.
    setForm((prev) => ({ ...prev, provincia: e.target.value.toUpperCase() }));
    setProvinciaAbierta(true);
    setProvinciaIdx(-1);
  };

  const elegirProvincia = (valor) => {
    setForm((prev) => ({ ...prev, provincia: valor }));
    setProvinciaAbierta(false);
    setProvinciaIdx(-1);
  };

  const onProvinciaBlur = () => {
    // Pequeño delay para que un click en una sugerencia se registre antes
    // de cerrar la lista.
    setTimeout(() => setProvinciaAbierta(false), 120);
    // Si lo tipeado matchea una provincia (ignorando acentos/mayúsculas),
    // se deja en su forma canónica exacta.
    const canonica = resolverProvincia(form.provincia);
    if (canonica && canonica !== form.provincia) {
      setForm((prev) => ({ ...prev, provincia: canonica }));
    }
  };

  const onProvinciaKeyDown = (e) => {
    if (!provinciaAbierta && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setProvinciaAbierta(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setProvinciaIdx((i) => Math.min(i + 1, provinciasSugeridas.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setProvinciaIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && provinciaAbierta && provinciaIdx >= 0) {
      e.preventDefault();
      elegirProvincia(provinciasSugeridas[provinciaIdx]);
    } else if (e.key === 'Escape') {
      setProvinciaAbierta(false);
    }
  };

  const handleAlta = async (e) => {
    e.preventDefault();
    setResultado(null);

    // La provincia SIEMPRE tiene que ser una de la lista fija — nunca texto
    // libre. Si no resuelve, se corta acá sin llamar al backend.
    const provincia = resolverProvincia(form.provincia);
    if (!provincia) {
      setResultado({ tipo: 'error', mensaje: 'Elegí una provincia de la lista.' });
      return;
    }

    setEnviando(true);
    const token = localStorage.getItem(CLAVE_TOKEN);
    try {
      const { data } = await axios.post(
        `${API_URL}/superadmin/municipios`,
        {
          subdominio: form.subdominio.trim(),
          nombre: form.nombre.trim(),
          provincia,
          admin: {
            nombre: form.adminNombre.trim(),
            apellido: form.adminApellido.trim(),
            cuil: form.adminCuil.trim(),
            email: form.adminEmail.trim(),
            telefono: form.adminTelefono.trim(),
          },
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResultado({
        tipo: data.estado === 'creado' ? 'exito' : 'aviso',
        mensaje: data.mensaje,
        url: data.municipio?.url,
        adminEmail: data.admin?.email,
      });
      if (data.estado === 'creado') setForm(FORM_VACIO);
    } catch (err) {
      if (err.response?.status === 401) {
        // Sesión de superadmin vencida: volver al login.
        cerrarSesion();
        setError('Tu sesión expiró. Ingresá de nuevo.');
        return;
      }
      setResultado({
        tipo: 'error',
        mensaje:
          err.response?.data?.mensaje
          || 'No se pudo completar el alta. Revisá los datos e intentá de nuevo.',
      });
      console.error('Error en alta de cliente:', err);
    } finally {
      setEnviando(false);
    }
  };

  const previewUrl = form.subdominio
    ? `${form.subdominio}.be-tech.com.ar`
    : 'subdominio.be-tech.com.ar';

  return (
    <div className="login-container">
      <div className="login-gradient"></div>

      <div className="login-card" style={me ? { maxWidth: 520 } : undefined}>
        <div className="login-logo">
          <img src="/logo-betech.svg" alt="Betech" className="logo-img" />
        </div>

        {me ? (
          <>
            <h1 className="login-title">{TITULOS_SECCION[seccion]}</h1>
            <p className="login-subtitle">
              Sesión de {me.email}
              {' · '}
              <button
                type="button"
                className="link-ayuda"
                onClick={cerrarSesion}
                style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', padding: 0 }}
              >
                Cerrar sesión
              </button>
            </p>

            <div role="tablist" aria-label="Secciones del panel" className="superadmin-tabs" style={estilosTabs.barra}>
              {[
                { id: 'alta', texto: 'Alta de cliente' },
                { id: 'clientes', texto: 'Clientes' },
                { id: 'demos', texto: 'Demos' },
                { id: 'seguridad', texto: 'Seguridad' },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={seccion === t.id}
                  onClick={() => setSeccion(t.id)}
                  className="superadmin-tab"
                  style={estilosTabs.tab(seccion === t.id)}
                >
                  {t.texto}
                </button>
              ))}
            </div>

            {/* Las secciones quedan montadas y se oculta la inactiva: así
                cambiar de pestaña no borra un alta a medio cargar ni un QR de
                2FA que todavía no se confirmó. (display:none inline porque
                .login-form define display:flex y pisaría al atributo hidden.)
                Clientes se vuelve a cargar solo cada vez que se entra a la
                pestaña (prop `activa`), así muestra el estado al día. */}
            <div style={seccion === 'clientes' ? undefined : { display: 'none' }}>
              <SuperadminClientes
                token={localStorage.getItem(CLAVE_TOKEN)}
                activa={seccion === 'clientes'}
                onSesionExpirada={() => {
                  cerrarSesion();
                  setError('Tu sesión expiró. Ingresá de nuevo.');
                }}
              />
            </div>

            <div style={seccion === 'demos' ? undefined : { display: 'none' }}>
              <SuperadminDemos
                token={localStorage.getItem(CLAVE_TOKEN)}
                activa={seccion === 'demos'}
                onSesionExpirada={() => {
                  cerrarSesion();
                  setError('Tu sesión expiró. Ingresá de nuevo.');
                }}
              />
            </div>

            <div style={seccion === 'seguridad' ? undefined : { display: 'none' }}>
              <SuperadminSeguridad
                token={localStorage.getItem(CLAVE_TOKEN)}
                activo={!!me.totp_habilitado}
                onCambio={() => cargarMe(localStorage.getItem(CLAVE_TOKEN))}
                onSesionExpirada={() => {
                  cerrarSesion();
                  setError('Tu sesión expiró. Ingresá de nuevo.');
                }}
              />
            </div>

            <form
              onSubmit={handleAlta}
              className="login-form"
              style={seccion === 'alta' ? undefined : { display: 'none' }}
            >
              <p style={subtituloSeccion}>Datos del municipio</p>

              <div className="form-group">
                <label htmlFor="nombre">Nombre del municipio</label>
                <div className="input-wrapper">
                  <span className="input-icon"><IconMunicipio size={16} stroke={STROKE_DEGRADE} /></span>
                  <input
                    id="nombre"
                    type="text"
                    placeholder="Municipalidad de Ejemplo"
                    value={form.nombre}
                    onChange={actualizarCampo('nombre')}
                    className="form-input"
                    disabled={enviando}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="subdominio">Subdominio</label>
                <div className="input-wrapper">
                  <span className="input-icon"><IconMunicipio size={16} stroke={STROKE_DEGRADE} /></span>
                  <input
                    id="subdominio"
                    type="text"
                    placeholder="ejemplo"
                    value={form.subdominio}
                    onChange={actualizarCampo('subdominio')}
                    className="form-input"
                    disabled={enviando}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                    required
                  />
                </div>
                <span style={textoAyuda}>
                  Minúsculas, números y guiones. Va a quedar en <strong>{previewUrl}</strong>
                </span>
              </div>

              <div className="form-group">
                <label htmlFor="provincia">Provincia</label>
                <div className="input-wrapper">
                  <span className="input-icon"><IconMunicipio size={16} stroke={STROKE_DEGRADE} /></span>
                  <input
                    id="provincia"
                    type="text"
                    role="combobox"
                    aria-expanded={provinciaAbierta}
                    aria-controls="provincia-opciones"
                    aria-autocomplete="list"
                    autoComplete="off"
                    autoCapitalize="characters"
                    spellCheck="false"
                    placeholder="Empezá a escribir: CHA…"
                    value={form.provincia}
                    onChange={onProvinciaChange}
                    onFocus={() => setProvinciaAbierta(true)}
                    onBlur={onProvinciaBlur}
                    onKeyDown={onProvinciaKeyDown}
                    className="form-input"
                    disabled={enviando}
                    required
                  />
                  {provinciaAbierta && provinciasSugeridas.length > 0 && (
                    <ul id="provincia-opciones" style={estilosCombo.lista} role="listbox">
                      {provinciasSugeridas.map((p, i) => (
                        <li
                          key={p}
                          role="option"
                          aria-selected={i === provinciaIdx}
                          onMouseDown={(ev) => { ev.preventDefault(); elegirProvincia(p); }}
                          onMouseEnter={() => setProvinciaIdx(i)}
                          style={{
                            ...estilosCombo.opcion,
                            ...(i === provinciaIdx ? estilosCombo.opcionActiva : null),
                            ...(p === provinciaCanonica ? estilosCombo.opcionElegida : null),
                          }}
                        >
                          {p}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {form.provincia && !provinciaCanonica && !provinciaAbierta && (
                  <span style={{ ...textoAyuda, color: 'var(--color-error)' }}>
                    Elegí una provincia de la lista.
                  </span>
                )}
              </div>

              <p style={subtituloSeccion}>Admin principal</p>

              <div className="form-group">
                <label htmlFor="adminNombre">Nombre</label>
                <div className="input-wrapper">
                  <span className="input-icon"><IconPersona size={16} stroke={STROKE_DEGRADE} /></span>
                  <input
                    id="adminNombre"
                    type="text"
                    placeholder="Juan"
                    value={form.adminNombre}
                    onChange={actualizarCampo('adminNombre')}
                    className="form-input"
                    disabled={enviando}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="adminApellido">Apellido</label>
                <div className="input-wrapper">
                  <span className="input-icon"><IconPersona size={16} stroke={STROKE_DEGRADE} /></span>
                  <input
                    id="adminApellido"
                    type="text"
                    placeholder="Pérez"
                    value={form.adminApellido}
                    onChange={actualizarCampo('adminApellido')}
                    className="form-input"
                    disabled={enviando}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="adminCuil">CUIL</label>
                <div className="input-wrapper">
                  <span className="input-icon"><IconDocumento size={16} stroke={STROKE_DEGRADE} /></span>
                  <input
                    id="adminCuil"
                    type="text"
                    inputMode="numeric"
                    placeholder="20-12345678-9"
                    value={form.adminCuil}
                    onChange={actualizarCampo('adminCuil')}
                    className="form-input"
                    disabled={enviando}
                    required
                  />
                </div>
                <span style={textoAyuda}>11 dígitos, con o sin guiones.</span>
              </div>

              <div className="form-group">
                <label htmlFor="adminEmail">Email</label>
                <div className="input-wrapper">
                  <span className="input-icon"><IconEmail size={16} stroke={STROKE_DEGRADE} /></span>
                  <input
                    id="adminEmail"
                    type="email"
                    placeholder="admin@ejemplo.gob.ar"
                    value={form.adminEmail}
                    onChange={actualizarCampo('adminEmail')}
                    className="form-input"
                    disabled={enviando}
                    required
                  />
                </div>
                <span style={textoAyuda}>Le llega la invitación para activar su cuenta.</span>
              </div>

              <div className="form-group">
                <label htmlFor="adminTelefono">Teléfono</label>
                <div className="input-wrapper">
                  <span className="input-icon"><IconPersona size={16} stroke={STROKE_DEGRADE} /></span>
                  <input
                    id="adminTelefono"
                    type="tel"
                    placeholder="3624-555123"
                    value={form.adminTelefono}
                    onChange={actualizarCampo('adminTelefono')}
                    className="form-input"
                    disabled={enviando}
                    required
                  />
                </div>
              </div>

              {resultado && (
                <div style={cajaResultado(resultado.tipo)}>
                  <span style={{ flexShrink: 0, marginTop: 1 }}>
                    {resultado.tipo === 'exito'
                      ? <IconExito size={16} stroke="currentColor" />
                      : <IconAdvertencia size={16} stroke="currentColor" />}
                  </span>
                  <div>
                    <div>{resultado.mensaje}</div>
                    {resultado.url && (
                      <div style={{ marginTop: 6, fontSize: 12 }}>
                        Dominio: <strong>{resultado.url.replace(/^https?:\/\//, '')}</strong>
                        {resultado.adminEmail && <> · Admin: <strong>{resultado.adminEmail}</strong></>}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <button
                type="submit"
                className="btn-ingresar"
                disabled={enviando || (!!form.provincia && !provinciaCanonica)}
              >
                {enviando ? 'Dando de alta… (puede tardar unos segundos)' : 'Dar de alta'}
              </button>
            </form>
          </>
        ) : token2fa ? (
          <>
            <h1 className="login-title">Verificación en dos pasos</h1>
            <p className="login-subtitle">Abrí tu app autenticadora e ingresá el código de 6 dígitos.</p>

            {/* key propia: sin ella React reutiliza los inputs del login (misma
                estructura) en vez de crear los nuevos, y el autoFocus del campo
                de código nunca se dispara. */}
            <form key="form-2fa" onSubmit={handleVerificar2fa} className="login-form">
              <div className="form-group">
                <label htmlFor="codigo2fa">Código</label>
                <div className="input-wrapper">
                  <span className="input-icon"><IconCandado size={16} stroke={STROKE_DEGRADE} /></span>
                  <input
                    id="codigo2fa"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="000000"
                    value={codigo2fa}
                    onChange={(e) => setCodigo2fa(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="form-input"
                    style={estiloInputCodigo}
                    readOnly={verificando}
                    autoFocus
                  />
                </div>
                <span style={textoAyuda}>Tenés 2 minutos para completar este paso.</span>
              </div>

              {error && <div className="error-message">{error}</div>}

              <button type="submit" className="btn-ingresar" disabled={verificando || codigo2fa.length !== 6}>
                {verificando ? 'Verificando...' : 'Verificar'}
              </button>
              <button
                type="button"
                className="link-ayuda"
                onClick={() => volverAlLogin('')}
                disabled={verificando}
                style={{ background: 'none', border: 'none', cursor: 'pointer', font: 'inherit', fontSize: 13, padding: 4 }}
              >
                Volver
              </button>
            </form>
          </>
        ) : (
          <>
            <h1 className="login-title">Superadmin</h1>
            <p className="login-subtitle">Acceso de nivel plataforma</p>

            <form key="form-login" onSubmit={handleLogin} className="login-form">
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <div className="input-wrapper">
                  <span className="input-icon"><IconEmail size={16} stroke={STROKE_DEGRADE} /></span>
                  <input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="form-input"
                    disabled={cargando}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="contrasena">Contraseña</label>
                <div className="input-wrapper">
                  <span className="input-icon"><IconContrasena size={16} stroke={STROKE_DEGRADE} /></span>
                  <input
                    id="contrasena"
                    type={mostrarContrasena ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={contrasena}
                    onChange={(e) => setContrasena(e.target.value)}
                    className="form-input"
                    disabled={cargando}
                    required
                  />
                  <button
                    type="button"
                    className="btn-mostrar-contraseña"
                    onClick={() => setMostrarContrasena(!mostrarContrasena)}
                    tabIndex="-1"
                  >
                    {mostrarContrasena
                      ? <IconOcultarContrasena size={16} stroke="#555" />
                      : <IconMostrarContrasena size={16} stroke="#555" />}
                  </button>
                </div>
              </div>

              {error && <div className="error-message">{error}</div>}

              <button type="submit" className="btn-ingresar" disabled={cargando}>
                {cargando ? 'Ingresando...' : 'Ingresar'}
              </button>
            </form>
          </>
        )}

        <div className="login-footer">
          <p>
            © 2026 Betech Solutions |
            <a href="https://be-tech.com.ar" target="_blank" rel="noopener noreferrer">
              {' '}www.be-tech.com.ar
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

// --- estilos puntuales de esta pantalla (el resto sale de login.css) ---
const TITULOS_SECCION = {
  alta: 'Alta de cliente',
  clientes: 'Clientes',
  demos: 'Invitaciones demo',
  seguridad: 'Seguridad',
};

const subtituloSeccion = {
  margin: '4px 0 0',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: '#7f8c8d',
  fontFamily: 'var(--font-header)',
};

const textoAyuda = {
  fontSize: 12,
  color: '#7f8c8d',
  lineHeight: 1.4,
};

// Input del código de 6 dígitos: centrado y con letras espaciadas para que
// se lea de un vistazo (el padding izquierdo deja lugar al ícono).
const estiloInputCodigo = {
  padding: '14px 14px 14px 40px',
  textAlign: 'center',
  letterSpacing: '8px',
  fontSize: 20,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
};

// Pestañas del panel logueado (Alta de cliente / Clientes / Seguridad).
const estilosTabs = {
  barra: {
    display: 'flex',
    // wrap: con 4 pestañas ya no entran en una sola fila en un celular (menos con
    // la tipografía real, Montserrat, que es más ancha que la de respaldo). En
    // pantallas angostas .superadmin-tab las pone de a dos (grilla 2×2, ver
    // login.css); más anchas, siguen en una sola fila.
    flexWrap: 'wrap',
    gap: 4,
    padding: 4,
    margin: '0 0 24px',
    background: '#f1f3f5',
    borderRadius: 12,
  },
  // flex 1 1 auto + nowrap: "Alta de cliente" no se parte en varias líneas a
  // 320px (cada pestaña toma el ancho de su texto y el resto se reparte parejo;
  // si la fila se llena, la barra hace wrap — ver `barra`).
  tab: (activa) => ({
    // flex: ver .superadmin-tab en login.css (necesita una media query).
    padding: '10px 6px',
    whiteSpace: 'nowrap',
    border: 'none',
    borderRadius: 9,
    // clamp: 13px en pantallas normales, baja hasta 11px en 320px para que
    // las tres pestañas entren en la barra sin cortarse.
    fontSize: 'clamp(11px, 3.4vw, 13px)',
    fontWeight: 600,
    fontFamily: 'var(--font-header)',
    cursor: 'pointer',
    background: activa ? '#ffffff' : 'transparent',
    color: activa ? 'var(--color-turquesa)' : '#7f8c8d',
    boxShadow: activa ? '0 1px 4px rgba(0, 0, 0, 0.12)' : 'none',
    transition: 'var(--transition)',
  }),
};

// Lista desplegable del autocompletado de Provincia. Va absoluta dentro
// de .input-wrapper (que es position: relative en login.css).
const estilosCombo = {
  lista: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    zIndex: 20,
    margin: 0,
    padding: 4,
    listStyle: 'none',
    background: '#ffffff',
    border: '2px solid #e0e0e0',
    borderRadius: 12,
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)',
    maxHeight: 220,
    overflowY: 'auto',
  },
  opcion: {
    padding: '10px 12px',
    borderRadius: 8,
    fontSize: 13,
    color: '#2c3e50',
    cursor: 'pointer',
  },
  opcionActiva: {
    background: 'rgba(0, 217, 163, 0.12)',
  },
  opcionElegida: {
    fontWeight: 700,
  },
};

const cajaResultado = (tipo) => {
  const colores = {
    exito: { fondo: 'rgba(0, 217, 163, 0.1)', color: '#0f8a6d', borde: '#00d9a3' },
    aviso: { fondo: 'rgba(243, 156, 18, 0.12)', color: '#9a6b00', borde: '#f39c12' },
    error: { fondo: 'rgba(231, 76, 60, 0.1)', color: '#e74c3c', borde: '#e74c3c' },
  };
  const c = colores[tipo] || colores.aviso;
  return {
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
    background: c.fondo,
    color: c.color,
    padding: '12px 14px',
    borderRadius: 12,
    fontSize: 13,
    borderLeft: `4px solid ${c.borde}`,
    lineHeight: 1.45,
  };
};

export default Superadmin;
